/**
 * Secure redirect validator.
 *
 * Validates redirect targets (e.g. from query params) so only allow-listed HTTPS
 * hosts are accepted.
 *
 * Only allows absolute HTTPS URLs.
 * Rejects:
 * - relative paths
 * - http, javascript:, data:, etc.
 * - malformed URLs
 *
 * Handles Vue Router query params: string | string[] | undefined
 */

type AllowedRedirect = string;
const ALLOWED_REDIRECT_HOSTS: readonly AllowedRedirect[] = ["api-internal.apiabroad.com"];

type RedirectInput = string | string[] | null | undefined;

/** Single allow-listed host (element type of the allow-list). */
type AllowedHost = AllowedRedirect;

interface RedirectOptions {
  /**
   * Optional hostname allow-list for redirects.
   * Empty array means allow no hosts (all redirects rejected).
   */
  allowedHosts?: readonly AllowedHost[];
}

/**
 * Returns a normalized URL if the input is a valid HTTPS URL whose host is
 * allow-listed; otherwise null.
 *
 * @param value - Raw redirect URL (e.g. from query), or array (first element used)
 * @param options - Optional overrides; `allowedHosts` replaces the default list
 * @returns Normalized URL string, or null if invalid
 * @example
 * getValidRedirectUrl("https://api-internal.apiabroad.com/path") // => "https://api-internal.apiabroad.com/path"
 * getValidRedirectUrl("http://evil.com") // => null
 */
export function getValidRedirectUrl(value: RedirectInput, options: RedirectOptions = {}): string | null {
  const allowedHosts = options.allowedHosts ?? ALLOWED_REDIRECT_HOSTS;

  if (value === null) {
    return null;
  }

  const raw = Array.isArray(value) ? value[0] : value;

  if (typeof raw !== "string") {
    return null;
  }

  const trimmed = raw.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const url = new URL(trimmed);

    // Only allow HTTPS
    if (url.protocol !== "https:") {
      return null;
    }

    // Enforce hostname allow-list (empty list => allow nothing)
    if (!allowedHosts.includes(url.hostname)) {
      return null;
    }

    // Return normalized URL
    return url.toString();
  } catch {
    return null;
  }
}
